import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sha256Hex } from "@/lib/crypto";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, Check, Share2 } from "lucide-react";
import { useEntitlements } from "@/hooks/useEntitlements";
import { UpgradeHint } from "@/components/UpgradeHint";

const EMOJI_OPTIONS = ["🧒", "👧", "👦", "🧒🏽", "👧🏿", "👦🏼", "🐱", "🦊", "🐶", "🦄", "🐻", "🐰"];

interface Child {
  id: string;
  name: string;
  avatar_emoji: string;
  family_id: string;
  custody_enabled?: boolean;
}

interface FamilyMember {
  id: string;
  user_id: string;
  display_name: string | null;
  child_facing_name: string | null;
  role: string;
}

interface ChildProfileDrawerProps {
  child: Child | null;
  onClose: () => void;
  familyId: string;
  members: FamilyMember[];
  userId: string | undefined;
  /** All children in the same family, used for multi-child invite flow */
  allFamilyChildren?: Child[];
  /** child_ids that already have a coparent or pending invite */
  childrenWithCoparent?: Set<string>;
}

export function ChildProfileDrawer({ child, onClose, familyId, members, userId, allFamilyChildren = [], childrenWithCoparent = new Set() }: ChildProfileDrawerProps) {
  const queryClient = useQueryClient();
  
  const [editName, setEditName] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [hasEdited, setHasEdited] = useState(false);
  const [showPairInput, setShowPairInput] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [pairLoading, setPairLoading] = useState(false);
  const [editMyDisplayName, setEditMyDisplayName] = useState("");
  const [editMyMemberName, setEditMyMemberName] = useState("");
  const [editChildFacingName, setEditChildFacingName] = useState("");

  // Siblings without a coparent (candidates for multi-child invite)
  // Reset state when child changes
  const [prevChildId, setPrevChildId] = useState<string | null>(null);
  if (child && child.id !== prevChildId) {
    setPrevChildId(child.id);
    setEditName(child.name);
    setEditEmoji(child.avatar_emoji);
    setHasEdited(false);
    setShowPairInput(false);
    setPairCode("");
    setEditMyDisplayName("");
  }

  // Hent gjeldende medforeldre for dette barnet fra child_coparents (inkl. display_name)
  const { data: childCoparents = [] } = useQuery({
    queryKey: ["child-coparents", child?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("child_coparents" as any)
        .select("id, coparent_user_id, display_name")
        .eq("child_id", child!.id);
      if (error) throw error;
      return data as unknown as { id: string; coparent_user_id: string; display_name: string | null }[];
    },
    enabled: !!child,
  });

  const currentCoparentId = childCoparents[0]?.coparent_user_id ?? null;
  // My own coparent entry (if I'm the coparent for this child)
  const myCoparentEntry = childCoparents.find((cp) => cp.coparent_user_id === userId);
  const otherCoparentEntry = childCoparents.find((cp) => cp.coparent_user_id !== userId);

  // Sync display name when coparent data loads
  const [prevMyDisplayName, setPrevMyDisplayName] = useState<string | undefined>(undefined);
  const currentMyDisplayName = myCoparentEntry?.display_name ?? "";
  if (currentMyDisplayName !== prevMyDisplayName) {
    setPrevMyDisplayName(currentMyDisplayName);
    setEditMyDisplayName(currentMyDisplayName);
  }

  // My family_members entry (for display name editing)
  const myMember = members.find((m) => m.user_id === userId);

  // Sync member display name
  const [prevMyMemberName, setPrevMyMemberName] = useState<string | undefined>(undefined);
  const currentMyMemberName = myMember?.display_name ?? "";
  if (currentMyMemberName !== prevMyMemberName) {
    setPrevMyMemberName(currentMyMemberName);
    setEditMyMemberName(currentMyMemberName);
  }

  // Sync child-facing name
  const [prevChildFacingName, setPrevChildFacingName] = useState<string | undefined>(undefined);
  const currentChildFacingName = (myMember as any)?.child_facing_name ?? "";
  if (currentChildFacingName !== prevChildFacingName) {
    setPrevChildFacingName(currentChildFacingName);
    setEditChildFacingName(currentChildFacingName);
  }

  // Fjern akseptert medforelder (sletter child_coparents)
  const removeCoparent = useMutation({
    mutationFn: async () => {
      if (!child) return;
      const { error } = await supabase
        .from("child_coparents" as any)
        .delete()
        .eq("child_id", child.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["child-coparents", child?.id] });
      queryClient.invalidateQueries({ queryKey: ["child-coparents-all", familyId] });
      toast.success("Medforelder fjernet");
    },
    onError: () => toast.error("Kunne ikke fjerne medforelder"),
  });

  // (Old pairing sessions query removed — now using device_pair_codes RPCs)

  // Device sessions query
  const { data: devices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ["child-devices", child?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("child_device_sessions")
        .select("id, device_label, last_seen_at, created_at, expires_at")
        .eq("child_id", child!.id)
        .is("revoked_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!child,
  });

  // Update child mutation
  const updateChild = useMutation({
    mutationFn: async () => {
      if (!child) return;
      const { error } = await supabase
        .from("children")
        .update({ name: editName.trim(), avatar_emoji: editEmoji })
        .eq("id", child.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["children", familyId] });
      queryClient.invalidateQueries({ queryKey: ["child", child?.id] });
      queryClient.invalidateQueries({ queryKey: ["all-children"] });
      setHasEdited(false);
      toast.success("Barn oppdatert");
    },
    onError: () => toast.error("Kunne ikke oppdatere"),
  });

  // Update display name on child_coparents
  const updateMyDisplayName = useMutation({
    mutationFn: async () => {
      if (!myCoparentEntry) return;
      const { error } = await supabase
        .from("child_coparents" as any)
        .update({ display_name: editMyDisplayName.trim() || null })
        .eq("id", myCoparentEntry.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["child-coparents", child?.id] });
      toast.success("Kallenavn oppdatert");
    },
    onError: () => toast.error("Kunne ikke oppdatere"),
  });

  // Update display name on family_members (shown on front page / to co-parents)
  const updateMyMemberName = useMutation({
    mutationFn: async () => {
      if (!myMember || !userId) return;
      const { error } = await supabase
        .from("family_members")
        .update({ display_name: editMyMemberName.trim() || null })
        .eq("family_id", familyId)
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-members"] });
      queryClient.invalidateQueries({ queryKey: ["family-memberships"] });
      toast.success("Visningsnavn oppdatert");
    },
    onError: () => toast.error("Kunne ikke oppdatere"),
  });

  // Update child-facing name on family_members (shown to children, e.g. "Pappa")
  const updateChildFacingName = useMutation({
    mutationFn: async () => {
      if (!myMember || !userId) return;
      const { error } = await supabase
        .from("family_members")
        .update({ child_facing_name: editChildFacingName.trim() || null } as any)
        .eq("family_id", familyId)
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-members", familyId] });
      queryClient.invalidateQueries({ queryKey: ["family-memberships"] });
      toast.success("Barnas navn på deg oppdatert");
    },
    onError: () => toast.error("Kunne ikke oppdatere"),
  });

  // Toggle custody_enabled per child
  const [localCustodyEnabled, setLocalCustodyEnabled] = useState(child?.custody_enabled ?? false);

  // Sync local state when child prop changes
  useEffect(() => {
    setLocalCustodyEnabled(child?.custody_enabled ?? false);
  }, [child?.custody_enabled]);

  const toggleCustody = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!child) return;
      const { error } = await supabase
        .from("children")
        .update({ custody_enabled: enabled } as any)
        .eq("id", child.id);
      if (error) throw error;
    },
    onMutate: (enabled) => {
      setLocalCustodyEnabled(enabled);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["children"] });
      queryClient.invalidateQueries({ queryKey: ["all-children"] });
    },
    onError: () => {
      setLocalCustodyEnabled(child?.custody_enabled ?? false);
      toast.error("Kunne ikke endre innstilling");
    },
  });

  // Revoke device mutation
  const revokeDevice = useMutation({
    mutationFn: async (deviceId: string) => {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { error } = await supabase
        .from("child_device_sessions")
        .update({ revoked_at: new Date().toISOString(), revoked_by_user_id: userId })
        .eq("id", deviceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["child-devices", child?.id] });
      toast.success("Tilgang fjernet");
    },
  });

  // Complete device pairing (new simplified flow)
  const completePairing = async () => {
    if (!child || pairCode.trim().length < 6) return;
    setPairLoading(true);
    try {
      const codeHash = await sha256Hex(pairCode.trim().toUpperCase());
      const { data, error: rpcErr } = await supabase.rpc("complete_device_pairing" as any, {
        _code_hash: codeHash,
        _child_id: child.id,
      });
      if (rpcErr) throw rpcErr;
      if (data === true) {
        queryClient.invalidateQueries({ queryKey: ["child-devices", child.id] });
        toast.success("Enhet koblet!");
        setShowPairInput(false);
        setPairCode("");
      } else {
        toast.error("Koden er ugyldig eller utløpt.");
      }
    } catch {
      toast.error("Kunne ikke koble enheten.");
    } finally {
      setPairLoading(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  const nameOrEmojiChanged = child && (editName !== child.name || editEmoji !== child.avatar_emoji);

  // Helper: find a member by user_id
  const getMemberName = (uid: string) =>
    members.find((m) => m.user_id === uid)?.display_name ?? "Medforelder";

  return (
    <Drawer open={!!child} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent className="mx-auto w-full max-w-md max-h-[90vh]">
        <DrawerHeader className="text-center">
          <DrawerTitle className="text-2xl">
            {child?.avatar_emoji} {child?.name}
          </DrawerTitle>
          <DrawerDescription>Rediger profil og enheter</DrawerDescription>
        </DrawerHeader>

        <ScrollArea className="px-4 pb-6 overflow-y-auto max-h-[calc(90vh-112px)]">
          <div className="space-y-5 pb-4">
            {/* ── Profile editing ── */}
            <section className="space-y-3">
              <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Profil</Label>
              <Input
                value={editName}
                onChange={(e) => { setEditName(e.target.value); setHasEdited(true); }}
                placeholder="Navn"
              />
              <div className="flex flex-wrap gap-1.5">
                {EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => { setEditEmoji(emoji); setHasEdited(true); }}
                    className={`rounded-md p-1.5 text-xl transition-all ${
                      editEmoji === emoji ? "bg-primary/10 ring-2 ring-primary" : "hover:bg-muted"
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              {nameOrEmojiChanged && (
                <Button
                  size="sm"
                  className="w-full rounded-xl"
                  onClick={() => updateChild.mutate()}
                  disabled={!editName.trim() || updateChild.isPending}
                >
                  {updateChild.isPending ? "Lagrer…" : "Lagre endringer"}
                </Button>
              )}
            </section>

            {/* ── Ditt fornavn / visningsnavn ── */}
            <Separator />
            <section className="space-y-2">
              <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                Ditt fornavn
              </Label>
              <p className="text-xs text-muted-foreground">
                Vises på forsiden og for medforeldre
              </p>
              <div className="flex gap-2">
                <Input
                  value={editMyMemberName}
                  onChange={(e) => setEditMyMemberName(e.target.value)}
                  placeholder="F.eks. Erik"
                  className="flex-1"
                />
                <Button
                  size="sm"
                  onClick={() => updateMyMemberName.mutate()}
                  disabled={updateMyMemberName.isPending || editMyMemberName === (myMember?.display_name ?? "")}
                >
                  Lagre
                </Button>
              </div>
            </section>

            {/* ── Barnas navn på deg ── */}
            <Separator />
            <section className="space-y-2">
              <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                Barnas navn på deg
              </Label>
              <p className="text-xs text-muted-foreground">
                F.eks. «Pappa», «Mamma» — vises for barna
              </p>
              <div className="flex gap-2">
                <Input
                  value={editChildFacingName}
                  onChange={(e) => setEditChildFacingName(e.target.value)}
                  placeholder="F.eks. Pappa"
                  className="flex-1"
                />
                <Button
                  size="sm"
                  onClick={() => updateChildFacingName.mutate()}
                  disabled={updateChildFacingName.isPending || editChildFacingName === ((myMember as any)?.child_facing_name ?? "")}
                >
                  Lagre
                </Button>
              </div>
            </section>

            {/* ── Kallenavn per barn (child_coparents display_name, only when coparent) ── */}
            {myCoparentEntry && (
              <>
                <Separator />
                <section className="space-y-2">
                  <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                    Ditt kallenavn for {child?.name}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    F.eks. «Pappa», «Mamma», «Bestefar» — vises for medforelderen
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={editMyDisplayName}
                      onChange={(e) => setEditMyDisplayName(e.target.value)}
                      placeholder="F.eks. Pappa"
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={() => updateMyDisplayName.mutate()}
                      disabled={updateMyDisplayName.isPending || editMyDisplayName === (myCoparentEntry.display_name ?? "")}
                    >
                      Lagre
                    </Button>
                  </div>
                </section>
              </>
            )}

            <Separator />

            {/* ── Samværsplan og koordinering (per-child toggle) ── */}
            <CoparentGatedSection child={child} localCustodyEnabled={localCustodyEnabled} toggleCustody={toggleCustody} familyId={familyId} />

            {/* ── Medforelder (read-only summary + link) ── */}
            {localCustodyEnabled && (
              <>
                <Separator />
                <section className="space-y-3">
                  <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                    Medforelder for {child?.name}
                  </Label>

                  {currentCoparentId ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2.5 text-sm">
                        <span>👤</span>
                        <span className="font-medium flex-1">{getMemberName(currentCoparentId)}</span>
                        <span className="text-xs text-muted-foreground">aktiv</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-destructive"
                        onClick={() => removeCoparent.mutate()}
                        disabled={removeCoparent.isPending}
                      >
                        Fjern medforelder
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        Ingen medforelder er lagt til ennå.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full rounded-xl"
                        onClick={() => {
                          onClose();
                          window.location.href = `/settings/invite-coparent?child=${child.id}`;
                        }}
                      >
                        Legg til medforelder
                      </Button>
                    </div>
                  )}
                </section>
              </>
            )}

            <Separator />

            {/* ── Devices ── */}
            <section className="space-y-3">
              <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                Enheter ({devices.length} aktive)
              </Label>

              {devices.length > 0 ? (
                <div className="space-y-2">
                  {devices.map((d, i) => (
                    <div key={d.id} className="rounded-lg bg-muted/50 px-3 py-2.5 text-sm space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{d.device_label || `Enhet ${i + 1}`}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive text-xs h-7"
                          onClick={() => revokeDevice.mutate(d.id)}
                          disabled={revokeDevice.isPending}
                        >
                          Fjern
                        </Button>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        <p>Sist sett: {formatDate(d.last_seen_at)}</p>
                        <p>Opprettet: {formatDate(d.created_at)}</p>
                        <p>Utløper: {formatDate(d.expires_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !devicesLoading && <p className="text-sm text-muted-foreground">Ingen enheter koblet til</p>
              )}

              {/* Pair new device (simplified) */}
              {showPairInput ? (
                <div className="rounded-lg bg-primary/5 p-3 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Åpne lenken på barnets enhet, eller skriv inn koden som vises der
                  </p>

                  {/* Share link section */}
                   <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Del lenke</p>
                    <code className="block text-xs bg-muted rounded-lg px-3 py-2 truncate select-all overflow-hidden">
                      {window.location.origin}/start
                    </code>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 min-w-0 rounded-xl"
                        onClick={async () => {
                          await navigator.clipboard.writeText(`${window.location.origin}/start`);
                          toast.success("Lenke kopiert!");
                        }}
                      >
                        <Copy className="h-4 w-4 mr-1 shrink-0" /> Kopier
                      </Button>
                      {typeof navigator.share === "function" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-0 rounded-xl"
                          onClick={() => {
                            navigator.share({
                              title: "Flyt",
                              text: child ? `Åpne denne lenken på ${child.name} sin enhet` : "Åpne denne lenken på barnets enhet",
                              url: `${window.location.origin}/start`,
                            }).catch(() => {});
                          }}
                        >
                          <Share2 className="h-4 w-4 mr-1 shrink-0" /> Del
                        </Button>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Code input section */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Eller skriv inn kode</p>
                    <Input
                      value={pairCode}
                      onChange={(e) => setPairCode(e.target.value.toUpperCase())}
                      placeholder="ABC123"
                      maxLength={6}
                      className="text-center text-xl font-mono tracking-[0.2em] rounded-xl"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 rounded-xl"
                      onClick={() => { setShowPairInput(false); setPairCode(""); }}
                    >
                      Avbryt
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 rounded-xl"
                      onClick={completePairing}
                      disabled={pairCode.trim().length < 6 || pairLoading}
                    >
                      {pairLoading ? "Kobler…" : "Koble"}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-xl"
                  onClick={() => setShowPairInput(true)}
                >
                  📱 Koble ny enhet
                </Button>
              )}
            </section>
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
}

// ═══════════════════════════════════════════════════════
// Sub-component: Custody/collaboration toggle with plan gate
// ═══════════════════════════════════════════════════════
function CoparentGatedSection({
  child,
  localCustodyEnabled,
  toggleCustody,
  familyId,
}: {
  child: { name: string } | null;
  localCustodyEnabled: boolean;
  toggleCustody: { mutate: (v: boolean) => void; isPending: boolean };
  familyId: string;
}) {
  const { hasTogether } = useEntitlements(familyId);

  if (!hasTogether) {
    return (
      <section className="space-y-3">
        <Label className="text-sm font-medium">Samværsplan og koordinering</Label>
        <UpgradeHint
          title="Stå samlet rundt barnet"
          description="Del oversikt og ansvar mellom voksne – så barnet møter en trygg og forutsigbar hverdag."
          targetPlan="together"
          ctaLabel="Aktiver Sammen for denne familien"
          useCheckout
          familyId={familyId}
        />
      </section>
    );
  }

  // Together users get the full toggle
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">Samværsplan og koordinering</Label>
          <p className="text-xs text-muted-foreground">
            Aktiver kalender og byttesjekklister for {child?.name}
          </p>
        </div>
        <Switch
          checked={localCustodyEnabled}
          onCheckedChange={(checked) => {
            toggleCustody.mutate(checked);
          }}
          disabled={toggleCustody.isPending}
        />
      </div>

      {localCustodyEnabled && (
        <p className="text-xs text-muted-foreground rounded-lg bg-muted/50 p-2.5">
          Samværskalender og byttesjekklister er nå tilgjengelig for {child?.name} fra hovedmenyen.
        </p>
      )}
    </section>
  );
}
